import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Alert,
  useTheme,
  alpha,
  Button,
  AlertTitle,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  GlobalStyles
} from '@mui/material';
import { motion } from 'framer-motion';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import CampaignIcon from '@mui/icons-material/Campaign';
import TimelineIcon from '@mui/icons-material/Timeline';
import TableViewIcon from '@mui/icons-material/TableView';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';

// Import enhanced components
import DashboardLayout from '../components/shared/DashboardLayout';

// Import existing dashboard tabs
import BudgetTab from '../components/dashboardTabs/BudgetTab';
import OOHTab from '../components/dashboardTabs/OOHTab';

// Import services
import CurrentDataService from '../services/CurrentDataService';
import SnapshotDataService from '../services/SnapshotDataService';
import { useSnapshotMonths } from '../hooks/useSnapshotMonths';

// Import tab components
import RegionBrandMonthlyTable from '../components/dashboardTabs/RegionBrandMonthlyTable';
import MonthlyByMonthTable from '../components/dashboardTabs/MonthlyByMonthTable';
import HierarchicalFinancialTable from '../components/charts/HierarchicalFinancialTable';

/**
 * CurrentDashboard - Unified Dashboard for both current and snapshot data
 * Automatically switches between current (live) and snapshot (historical) data based on selected month
 * - Current month = live data from CurrentDataService
 * - Previous months = snapshot data from SnapshotDataService
 */
const CurrentDashboard = ({ availableSnapshots = [], onSnapshotSelect, initialSelectedMonth = '' }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing] = useState(false);
  
  // Snapshot states
  const [selectedMonth, setSelectedMonth] = useState(initialSelectedMonth || ''); // Current selected month (empty = current data)
  const [isSnapshotMode, setIsSnapshotMode] = useState(false); // Whether we're showing snapshot data

  // Use optimized hook for snapshot months (only if not provided as prop)
  const { 
    months: hookSnapshotMonths
  } = useSnapshotMonths({
    autoFetch: availableSnapshots.length === 0, // Only fetch if not provided
    debounceDelay: 300,
    forceRefresh: false
  });

  // Use provided snapshots or hook results
  const snapshotMonths = availableSnapshots.length > 0 ? availableSnapshots : hookSnapshotMonths;

  /**
   * Calculate savings from snapshot data using correct logic
   * @param {Object} data - Snapshot data
   * @returns {number} Total savings
   */
  const calculateSavingsFromSnapshotData = useCallback((data) => {
    try {
      let totalSavings = 0;
      
      // Process campaigns data
      const campaigns = data.campaigns || data.savingsDetail || [];
      
      campaigns.forEach(campaign => {
        const mediaTypes = campaign.media_types || {};
        const mediaTypesArray = Array.isArray(mediaTypes) ? mediaTypes : Object.values(mediaTypes);
        
        mediaTypesArray.forEach(mt => {
          if (!mt || typeof mt !== 'object') return;
          
          const monthlyData = mt.monthly_data || [];
          if (Array.isArray(monthlyData)) {
            monthlyData.forEach(md => {
              if (md.monthly_status === 'actual') {
                const provisionalSaving = parseFloat(md.provisional_saving || 0);
                if (!isNaN(provisionalSaving)) {
                  totalSavings += provisionalSaving;
                }
              }
            });
          }
        });
      });
      
      console.log('💰 Calculated savings from snapshot data:', totalSavings);
      return totalSavings;
    } catch (error) {
      console.error('Error calculating savings from snapshot data:', error);
      return 0;
    }
  }, []);

  /**
   * Process snapshot data to match current data format for overview tab
   * @param {Object} data - Snapshot data from SnapshotDataService
   * @param {string} month - Selected month
   * @returns {Object} Processed data for overview tab
   */
  const processSnapshotOverviewData = useCallback((data, month) => {
    try {
      // Extract financial metrics from snapshot data
      const enhancedMetrics = data.enhancedMetrics || {};
      
      // Tính saving đúng cách nếu không có từ enhancedMetrics
      let totalSavings = enhancedMetrics.total_savings || enhancedMetrics.financial_metrics?.total_savings || 0;
      
      // Fallback: tính lại saving từ snapshot data nếu cần
      if (totalSavings === 0 || totalSavings === null) {
        totalSavings = calculateSavingsFromSnapshotData(data);
      }
      
      const summaryCards = {
        givenCost: enhancedMetrics.total_budget || enhancedMetrics.financial_metrics?.total_budget || 0,
        planningCost: enhancedMetrics.total_planning || enhancedMetrics.financial_metrics?.total_planning || 0,
        actualCost: enhancedMetrics.total_actual || enhancedMetrics.financial_metrics?.total_actual || 0,
        totalSavings: totalSavings,
        totalCampaigns: enhancedMetrics.total_campaigns || enhancedMetrics.financial_metrics?.total_campaigns || 0
      };

      // Process brand summaries for cost breakdown
      const costBreakdownData = (data.brandSummaries || []).map(brand => ({
        name: brand.brand_name || 'Unknown Brand',
        given: brand.brand_budget || brand.total_given || 0,
        planning: brand.brand_planning || brand.total_planning || 0,
        actual: brand.brand_actual || brand.total_actual || 0,
        savings: brand.brand_savings || brand.total_savings || 0
      }));

      // Default empty arrays for missing data
      const mediaTypeSavings = [];
      const digitalVendorSavings = [];
      const performanceTableData = data.savingsDetail || [];

      return {
        summaryCards,
        costBreakdownData,
        mediaTypeSavings,
        digitalVendorSavings,
        performanceTableData,
        performanceRatios: {},
        dataSource: {
          summaryCards: 'Snapshot',
          costBreakdown: 'Snapshot',
          mediaTypeSavings: 'Snapshot',
          digitalVendorSavings: 'Snapshot',
          performanceTable: 'Snapshot'
        }
      };
    } catch (error) {
      console.error('Error processing snapshot overview data:', error);
      return {
        summaryCards: { givenCost: 0, planningCost: 0, actualCost: 0, totalSavings: 0, totalCampaigns: 0 },
        costBreakdownData: [],
        mediaTypeSavings: [],
        digitalVendorSavings: [],
        performanceTableData: [],
        performanceRatios: {},
        dataSource: { summaryCards: 'Error', costBreakdown: 'Error', mediaTypeSavings: 'Error', digitalVendorSavings: 'Error', performanceTable: 'Error' }
      };
    }
  }, [calculateSavingsFromSnapshotData]);

  /**
   * Smart data fetching - automatically chooses current or snapshot data
   * @param {string} month - Optional month filter (YYYY-MM format)
   */
  const fetchDashboardData = useCallback(async (month = selectedMonth) => {
    setLoading(true);
    setError(null);
    
    // Calculate month type outside try block for use in catch block
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
    const isCurrentMonth = !month || month === currentMonth;
    
    setIsSnapshotMode(!isCurrentMonth);
    
    try {
      
      console.log(`🔄 Fetching ${isCurrentMonth ? 'current' : 'snapshot'} dashboard data...`, {
        selectedMonth: month,
        currentMonth,
        isCurrentMonth
      });
      
      let data;
      
      if (isCurrentMonth) {
        // Use CurrentDataService for current month (optimized core endpoints only)
        console.log('📊 Using CurrentDataService for live data (optimized)');
        data = await CurrentDataService.getAllDashboardData();
      } else {
        // Use SnapshotDataService for historical data
        console.log(`📊 Using SnapshotDataService for snapshot data: ${month}`);
        const [snapshotData, budgetData] = await Promise.all([
          SnapshotDataService.getSnapshotData(month),
          SnapshotDataService.getBudgetData(month)
        ]);
        
        // Transform snapshot data to match current data format
        // Handle both new and old snapshot structures
        if (snapshotData.dataSource === 'NewSnapshotDatabase') {
          // New snapshot structure (3 tables)
          console.log('📊 Using new snapshot structure data');
          data = {
            enhancedMetrics: snapshotData.enhancedMetrics,
            brandSummaries: snapshotData.brandSummaries || [],
            savingsDetail: snapshotData.campaigns || [],
            oohCampaigns: snapshotData.oohCampaigns || [],
            budgetData: budgetData || [], // Use dedicated budget API data
            monthlyData: snapshotData.monthlyData || []
          };
        } else {
          // Old snapshot structure (JSON-based)
          console.log('📊 Using old snapshot structure data');
          data = {
            enhancedMetrics: snapshotData.enhancedMetrics,
            brandSummaries: snapshotData.brandSummaries || [],
            savingsDetail: snapshotData.campaigns || [],
            oohCampaigns: snapshotData.campaigns?.filter(c => 
              c.media_types && Object.values(c.media_types).some(mt => 
                mt.media_type?.toLowerCase() === 'ooh'
              )
            ) || [],
            budgetData: budgetData || [] // Use dedicated budget API data
          };
        }
      }
      
      console.log('📊 Raw dashboard data received:', {
        enhancedMetrics: data.enhancedMetrics ? 'Available' : 'Missing',
        enhancedMetricsStructure: data.enhancedMetrics ? Object.keys(data.enhancedMetrics) : 'N/A',
        financialMetrics: data.enhancedMetrics?.financial_metrics ? 'Available' : 'Missing',
        financialMetricsStructure: data.enhancedMetrics?.financial_metrics ? Object.keys(data.enhancedMetrics.financial_metrics) : 'N/A',
        brandSummaries: Array.isArray(data.brandSummaries) ? `${data.brandSummaries.length} brands` : 'Missing',
        savingsDetail: Array.isArray(data.savingsDetail) ? `${data.savingsDetail.length} items` : 'Missing',
        oohCampaigns: Array.isArray(data.oohCampaigns) ? `${data.oohCampaigns.length} campaigns` : 'Missing'
      });

      // Process data for overview tab
      const processedOverviewData = isCurrentMonth 
        ? CurrentDataService.processOverviewData(data)
        : processSnapshotOverviewData(data, month);
      
      console.log('✅ Processed overview data:', {
        dataType: isCurrentMonth ? 'current' : 'snapshot',
        month: month || 'current',
        summaryCards: processedOverviewData.summaryCards,
        costBreakdownData: processedOverviewData.costBreakdownData?.length || 0,
        mediaTypeSavings: processedOverviewData.mediaTypeSavings?.length || 0,
        digitalVendorSavings: processedOverviewData.digitalVendorSavings?.length || 0,
        performanceTableData: processedOverviewData.performanceTableData?.length || 0,
        dataSource: processedOverviewData.dataSource
      });

      // Debug: Log enhanced metrics structure for chip data
      console.log('🔍 Enhanced Metrics Structure for Chips:', {
        rawEnhancedMetrics: data.enhancedMetrics,
        financialMetrics: data.enhancedMetrics?.financial_metrics,
        summaryCardsData: processedOverviewData.summaryCards
      });

      // Set the processed data with metadata
      setDashboardData({
        ...data,
        processedOverview: processedOverviewData,
        dataType: isCurrentMonth ? 'current' : 'snapshot',
        selectedMonth: month,
        isSnapshotMode: !isCurrentMonth
      });
      
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      setError(`Failed to load ${isCurrentMonth ? 'current' : 'snapshot'} dashboard data: ${error.message}`);
      
      // Set fallback data to prevent UI crashes
      setDashboardData({
        enhancedMetrics: null,
        brandSummaries: [],
        savingsDetail: [],
        oohCampaigns: [],
        budgetData: [],
        processedOverview: {
          summaryCards: { givenCost: 0, planningCost: 0, actualCost: 0, totalSavings: 0, totalCampaigns: 0 },
          costBreakdownData: [],
          mediaTypeSavings: [],
          digitalVendorSavings: [],
          performanceTableData: [],
          performanceRatios: {},
          dataSource: { summaryCards: 'Fallback', costBreakdown: 'Fallback', mediaTypeSavings: 'Fallback', digitalVendorSavings: 'Fallback', performanceTable: 'Fallback' }
        },
        dataType: isCurrentMonth ? 'current' : 'snapshot',
        selectedMonth: month,
        isSnapshotMode: !isCurrentMonth
      });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, processSnapshotOverviewData]);

  // Snapshot months are now handled by the useSnapshotMonths hook

  /**
   * Handle month selection - switches between current and snapshot data
   */
  const handleMonthSelect = (month) => {
    console.log('📅 Month selected:', month);
    setSelectedMonth(month || '');
    
    // Fetch data for the selected month
    fetchDashboardData(month);
  };
  

  /**
   * Load data on component mount
   */
  useEffect(() => {
    fetchDashboardData(initialSelectedMonth);
  }, [initialSelectedMonth, fetchDashboardData]);
  
  /**
   * Update selected month when initialSelectedMonth changes (from URL)
   */
  useEffect(() => {
    // Only set when a non-empty initialSelectedMonth is provided
    if (initialSelectedMonth) {
      setSelectedMonth(initialSelectedMonth);
    }
  }, [initialSelectedMonth]);

  /**
   * Handle tab change
   */
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };


  /**
   * Animation variants
   */
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: 'spring',
        stiffness: 300, 
        damping: 20
      }
    }
  };

  /**
   * Tab configuration - Updated with new tabs
   */
  const tabs = [
    { 
      label: 'Budget and Given', 
      icon: <TableViewIcon />,
      iconPosition: 'start'
    },
    { 
      label: 'TRACKING BY BU', 
      icon: <TableViewIcon />,
      iconPosition: 'start'
    },
    { 
      label: 'TOTAL cost by brand', 
      icon: <CalendarViewMonthIcon />,
      iconPosition: 'start'
    },
    { 
      label: 'OOH Budget', 
      icon: <CampaignIcon />,
      iconPosition: 'start'
    },
    { 
      label: 'Campaign schedule', 
      icon: <PieChartIcon />,
      iconPosition: 'start'
    }
  ];


  /**
   * Process data for HierarchicalFinancialTable with media type separation
   * ICON section: Social media types
   * RS COST section: Other media types (OOH, Digital non-social, TV, Radio, etc.)
   */
  const processFinancialTableData = () => {
    if (!dashboardData) return [];

    try {
      console.log('🔄 Processing financial table data with media type separation...');
      
      // Extract data from various sources
      const brandSummaries = dashboardData.brandSummaries || [];
      const savingsDetail = dashboardData.savingsDetail || [];
      const budgetData = dashboardData.budgetData || [];
      
      // Helper function to determine if media type is social
      const isSocialMediaType = (mediaType, subMediaType, digitalVendor) => {
        if (!mediaType) return false;
        
        const type = mediaType.toLowerCase();
        const subType = (subMediaType || '').toLowerCase();
        const vendor = (digitalVendor || '').toLowerCase();
        
        // Check for social indicators
        return type.includes('social') || 
               subType.includes('social') ||
               vendor.includes('facebook') ||
               vendor.includes('meta') ||
               vendor.includes('instagram') ||
               vendor.includes('twitter') ||
               vendor.includes('linkedin') ||
               vendor.includes('tiktok') ||
               vendor.includes('snapchat') ||
               subType.includes('sns') ||
               subType.includes('social media');
      };

      // Helper function to process individual data items
      const processDataItem = (item, source = 'brand') => {
        const mediaType = item.media_type || item.mediaType || 'Mixed Media';
        const subMediaType = item.sub_media_type || item.subMediaType || null;
        const digitalVendor = item.digital_vendor || item.digitalVendor || null;
        
        const isSocial = isSocialMediaType(mediaType, subMediaType, digitalVendor);
        
        console.log(`📊 Processing ${source} item:`, {
          brand: item.brand_name || item.brand,
          campaign: item.campaign_name,
          mediaType,
          subMediaType,
          digitalVendor,
          isSocial
        });

        // Base data structure
        const baseData = {
          brand_name: item.brand_name || item.brand || 'Unknown Brand',
          campaign_name: item.campaign_name || `${item.brand_name} Campaign`,
          media_type: mediaType,
          sub_media_type: subMediaType,
          digital_vendor: digitalVendor,
          given_paid_media: parseFloat(item.given_cost || item.total_given || item.given || 0),
          given_social: parseFloat(item.given_social || 0),
          planning_gross_cost: parseFloat(item.planning_cost || item.total_planning || item.planning_gross_cost || 0),
          actual_gross_cost: parseFloat(item.actual_cost || item.total_actual || item.actual_gross_cost || 0),
          long_term_planning: parseFloat(item.long_term_planning || item.planning_cost || item.total_planning || item.planning_gross_cost || 0),
          short_term: parseFloat(item.short_term || 0),
          provisional_actual_cost: parseFloat(item.provisional_actual_cost || 0),
          invoice_actual_cost: parseFloat(item.invoice_actual_cost || 0),
          net_net_cost: parseFloat(item.net_net_cost || 0),
          tracking_fees: parseFloat(item.tracking_fees || 0),
          whtax: parseFloat(item.whtax || 0),
          ooscope_fees: parseFloat(item.ooscope_fees || 0),
          cost_on_coupon_voucher_etc: parseFloat(item.cost_on_coupon_voucher_etc || 0),
          efficiency: parseFloat(item.efficiency || 0),
          ooh_format: item.ooh_format || null,
          region_focus: item.region_focus || null,
          digital_ad_format: item.digital_ad_format || null,
          asset_video_length_image_type: item.asset_video_length_image_type || null,
          
          // Add section identifier for table processing
          section: isSocial ? 'ICON' : 'RS_COST',
          is_social: isSocial
        };

        return baseData;
      };

      const processedData = [];

      // Process brand summaries
      console.log(`📊 Processing ${brandSummaries.length} brand summaries...`);
      brandSummaries.forEach((brand, index) => {
        const brandData = processDataItem(brand, `brand-${index}`);
        processedData.push(brandData);
      });

      // Process savings detail for more granular data
      console.log(`📊 Processing ${savingsDetail.length} savings detail items...`);
      savingsDetail.forEach((item, index) => {
        const detailData = processDataItem(item, `savings-${index}`);
        processedData.push(detailData);
      });

      // Process budget data if available
      console.log(`📊 Processing ${budgetData.length} budget data items...`);
      budgetData.forEach((item, index) => {
        const budgetItem = processDataItem(item, `budget-${index}`);
        processedData.push(budgetItem);
      });

      // Group data by section
      const socialData = processedData.filter(item => item.section === 'ICON');
      const rsCostData = processedData.filter(item => item.section === 'RS_COST');

      console.log('📊 Data separation results:', {
        total: processedData.length,
        social: socialData.length,
        rsCost: rsCostData.length,
        socialItems: socialData.map(item => `${item.brand_name} - ${item.media_type}`),
        rsCostItems: rsCostData.map(item => `${item.brand_name} - ${item.media_type}`)
      });
      
      // If no real data available, return sample data with proper media type separation
      if (processedData.length === 0) {
        console.log('📊 No real data found, returning sample data with media type separation...');
        return [
          // Social media data for ICON section
          {
            brand_name: 'Heineken',
            campaign_name: 'Heineken Digital Social Campaign 2024',
            media_type: 'Digital',
            sub_media_type: 'Social Media',
            digital_vendor: 'Meta',
            given_paid_media: 1200000000,
            given_social: 800000000,
            planning_gross_cost: 1100000000,
            actual_gross_cost: 1050000000,
            long_term_planning: 1000000000,
            short_term: 100000000,
            provisional_actual_cost: 1020000000,
            invoice_actual_cost: 1050000000,
            net_net_cost: 980000000,
            tracking_fees: 30000000,
            whtax: 25000000,
            ooscope_fees: 10000000,
            cost_on_coupon_voucher_etc: 5000000,
            efficiency: 95.5,
            ooh_format: null,
            region_focus: 'National',
            digital_ad_format: 'Social Video + Display',
            asset_video_length_image_type: '15s Video',
            section: 'ICON',
            is_social: true
          },
          {
            brand_name: 'Tiger',
            campaign_name: 'Tiger Social Engagement 2024',
            media_type: 'Digital',
            sub_media_type: 'Social Media',
            digital_vendor: 'TikTok',
            given_paid_media: 800000000,
            given_social: 500000000,
            planning_gross_cost: 750000000,
            actual_gross_cost: 720000000,
            long_term_planning: 700000000,
            short_term: 50000000,
            provisional_actual_cost: 710000000,
            invoice_actual_cost: 720000000,
            net_net_cost: 680000000,
            tracking_fees: 20000000,
            whtax: 18000000,
            ooscope_fees: 2000000,
            cost_on_coupon_voucher_etc: 0,
            efficiency: 96.0,
            ooh_format: null,
            region_focus: 'Urban',
            digital_ad_format: 'Short Video',
            asset_video_length_image_type: '9s Video',
            section: 'ICON',
            is_social: true
          },
          // Non-social media data for RS COST section
          {
            brand_name: 'Heineken',
            campaign_name: 'Heineken OOH Premium Campaign 2024',
            media_type: 'OOH',
            sub_media_type: 'Billboard',
            digital_vendor: null,
            given_paid_media: 1500000000,
            given_social: 0,
            planning_gross_cost: 1400000000,
            actual_gross_cost: 1350000000,
            long_term_planning: 1300000000,
            short_term: 100000000,
            provisional_actual_cost: 1330000000,
            invoice_actual_cost: 1350000000,
            net_net_cost: 1300000000,
            tracking_fees: 35000000,
            whtax: 27000000,
            ooscope_fees: 15000000,
            cost_on_coupon_voucher_etc: 8000000,
            efficiency: 96.4,
            ooh_format: 'Premium Billboard',
            region_focus: 'Metro Manila',
            digital_ad_format: null,
            asset_video_length_image_type: null,
            section: 'RS_COST',
            is_social: false
          },
          {
            brand_name: 'Tiger',
            campaign_name: 'Tiger Digital Performance Campaign 2024',
            media_type: 'Digital',
            sub_media_type: 'Video',
            digital_vendor: 'YouTube',
            given_paid_media: 900000000,
            given_social: 0,
            planning_gross_cost: 850000000,
            actual_gross_cost: 820000000,
            long_term_planning: 800000000,
            short_term: 50000000,
            provisional_actual_cost: 810000000,
            invoice_actual_cost: 820000000,
            net_net_cost: 780000000,
            tracking_fees: 25000000,
            whtax: 20000000,
            ooscope_fees: 5000000,
            cost_on_coupon_voucher_etc: 0,
            efficiency: 96.5,
            ooh_format: null,
            region_focus: 'National',
            digital_ad_format: 'Video Pre-roll',
            asset_video_length_image_type: '30s Video',
            section: 'RS_COST',
            is_social: false
          }
        ];
      }
      
      return processedData;

    } catch (error) {
      console.error('❌ Error processing financial table data:', error);
      // Return sample data on error
      return [
        {
          brand_name: 'Sample Brand',
          campaign_name: 'Sample Campaign',
          media_type: 'Digital',
          sub_media_type: 'Display',
          digital_vendor: 'Sample Vendor',
                     given: 1000000000,
           given_social: 150000000,
           planning_gross_cost: 950000000,
           long_term_planning: 900000000,
           short_term: 50000000,
           actual_gross_cost: 900000000,
           provisional_actual_cost: 880000000,
           invoice_actual_cost: 900000000,
           net_net_cost: 850000000,
           tracking_fees: 25000000,
           whtax: 18000000,
           ooscope_fees: 7000000,
           cost_on_coupon_voucher_etc: 0,
           efficiency: 94.7,
           ooh_format: 'Billboard',
           region_focus: 'Metro',
           digital_ad_format: 'Display',
           asset_video_length_image_type: 'Static Image'
        }
      ];
    }
  };

  /**
   * Render dashboard header - REMOVED
   */
  // const renderDashboardHeader = () => {
  //   return (
  //     <motion.div variants={itemVariants}>
  //       <Card 
  //         elevation={0}
  //         sx={{ 
  //           borderRadius: 3,
  //           border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  //           background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.light, 0.02)})`,
  //           mb: 3
  //         }}
  //       >
  //         <CardContent sx={{ p: 3 }}>
  //           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
  //               <LiveTvIcon 
  //                 sx={{ 
  //                   color: theme.palette.success.main,
  //                   fontSize: '2rem'
  //                 }} 
  //               />
  //               <Box>
  //                 <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
  //                   Live Dashboard
  //                 </Typography>
  //                 <Typography variant="body2" color="text.secondary">
  //                   Real-time campaign data and analytics
  //                 </Typography>
  //               </Box>
  //             </Box>
  //             
  //             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
  //               <Box sx={{ textAlign: 'right' }}>
  //                 <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
  //                   Last Updated
  //                 </Typography>
  //                 <Typography variant="body2" sx={{ fontWeight: 500 }}>
  //                   {new Date().toLocaleTimeString()}
  //                 </Typography>
  //               </Box>
  //               
  //               <Chip 
  //                 label="Live"
  //                 color="success"
  //                 variant="filled"
  //                 size="small"
  //                 icon={<LiveTvIcon />}
  //               />
  //               
  //               <GlowButton
  //                 variant="outlined"
  //                 color="success"
  //                 size="small"
  //                 onClick={handleRefresh}
  //                 disabled={loading}
  //                 startIcon={<RefreshIcon />}
  //               >
  //                 Refresh
  //               </GlowButton>
  //             </Box>
  //           </Box>
  //         </CardContent>
  //       </Card>
  //     </motion.div>
  //   );
  // };

  /**
   * Render tab content based on active tab
   */
  const formatNumberToBillions = (num) => {
    if (typeof num !== 'number' || isNaN(num)) {
      return '0.0B';
    }
    // Convert to billions and format to one decimal place
    return `${(num / 1000000000).toFixed(1)}B`;
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        const metrics = dashboardData?.enhancedMetrics?.financial_metrics || dashboardData?.enhancedMetrics || {
          total_planning: 0,
          total_actual: 0,
          ytg_active: 0,
          total_savings: 0,
        };
        // Debug: Log metrics data for chips
        console.log('🔍 Chip Metrics Debug:', {
          rawMetrics: metrics,
          total_planning: metrics.total_planning,
          total_actual: metrics.total_actual,
          ytg_active: metrics.ytg_active,
          total_savings: metrics.total_savings,
          dashboardDataKeys: Object.keys(dashboardData || {}),
          enhancedMetricsKeys: Object.keys(dashboardData?.enhancedMetrics || {}),
          financialMetricsKeys: Object.keys(dashboardData?.enhancedMetrics?.financial_metrics || {})
        });

        const summaryChips = [
          { label: 'Total Planning', value: formatNumberToBillions(metrics.total_planning), color: 'primary' },
          { label: 'Total YTD', value: formatNumberToBillions(metrics.total_actual), color: 'info' },
          { label: 'Total YTG', value: formatNumberToBillions(metrics.ytg_active), color: 'warning' },
          { label: 'Total Savings (Backend)', value: formatNumberToBillions(metrics.total_savings), color: 'success' }
        ];

        return (
          <Box sx={{ width: '100%' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4, px: { xs: 1, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 4,
                    height: 32,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    borderRadius: 2,
                    mr: 2
                  }}
                />
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    mb: 0.5, 
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    lineHeight: 1.2
                  }}>
                    📊 Financial Overview {isSnapshotMode ? '(Historical)' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.4
                  }}>
                    {isSnapshotMode 
                      ? `Historical financial metrics from ${selectedMonth}` 
                      : 'Real-time financial metrics and budget analysis'
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Summary Cards */}
            <Box sx={{ px: { xs: 1, sm: 2 }, mb: 3 }}>
              <Grid container spacing={2}>
                {summaryChips.map((chip, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette[chip.color].main, 0.05)}, ${alpha(theme.palette[chip.color].light, 0.02)})`,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 25px ${alpha(theme.palette[chip.color].main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette[chip.color].main, 0.2)}`,
                        }
                      }}
                    >
                      {/* Accent bar */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${theme.palette[chip.color].main}, ${theme.palette[chip.color].light})`,
                        }}
                      />
                      
                      <CardContent sx={{ p: 2.5, pb: '20px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {chip.label}
                        </Typography>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${alpha(theme.palette[chip.color].main, 0.1)}, ${alpha(theme.palette[chip.color].main, 0.05)})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {chip.color === 'primary' && <TrendingUpIcon sx={{ color: theme.palette[chip.color].main, fontSize: 16 }} />}
                            {chip.color === 'info' && <TimelineIcon sx={{ color: theme.palette[chip.color].main, fontSize: 16 }} />}
                            {chip.color === 'warning' && <PieChartIcon sx={{ color: theme.palette[chip.color].main, fontSize: 16 }} />}
                            {chip.color === 'success' && <CampaignIcon sx={{ color: theme.palette[chip.color].main, fontSize: 16 }} />}
                          </Box>
                        </Box>
                        
                      <Typography 
                        variant="h4" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          mb: 0.5,
                          fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                          lineHeight: 1.1
                        }}
                      >
                        {chip.value}
                      </Typography>
                        
                        {loading && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <CircularProgress size={14} sx={{ mr: 1, color: theme.palette[chip.color].main }} />
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              lineHeight: 1.2
                            }}>
                              Updating...
                            </Typography>
                          </Box>
                        )}
                        
                        {!loading && (
                          <Typography 
                            variant="caption" 
                            color={`${chip.color}.main`}
                            sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {isSnapshotMode ? 'Historical Data' : 'Live Data'}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Financial Table */}
            <Box sx={{ px: { xs: 1, sm: 2 }, mt: 1 }}>
            <HierarchicalFinancialTable
                key={`fin-${isSnapshotMode ? 'snap' : 'cur'}-${selectedMonth || 'now'}`}
                data={processFinancialTableData()}
                loading={loading}
                showSummary={true}
                isSnapshotMode={isSnapshotMode}
                selectedSnapshotMonth={selectedMonth ? parseInt(selectedMonth.split('-')[1]) : null}
                selectedSnapshotMonthStr={selectedMonth || null}
                sx={{
                  '& .MuiTableContainer-root': {
                    maxWidth: '100%',
                    overflow: 'auto'
                  }
                }}
              />
            </Box>
          </Box>
        );
      case 1:
        return (
          <RegionBrandMonthlyTable 
            key={`rbmt-${isSnapshotMode ? 'snap' : 'cur'}-${selectedMonth || 'now'}`}
            loading={loading}
            selectedSnapshotMonth={isSnapshotMode ? selectedMonth : null}
            selectedSnapshotMonthStr={isSnapshotMode ? selectedMonth : null}
            isSnapshotMode={isSnapshotMode}
            snapshotData={isSnapshotMode ? dashboardData : null}
            enhancedMetrics={dashboardData?.enhancedMetrics}
          />
        );
      case 2:
        return (
          <MonthlyByMonthTable 
            key={`mbm-${isSnapshotMode ? 'snap' : 'cur'}-${selectedMonth || 'now'}`}
            loading={loading}
            selectedSnapshotMonth={isSnapshotMode ? selectedMonth : null}
            selectedSnapshotMonthStr={isSnapshotMode ? selectedMonth : null}
            isSnapshotMode={isSnapshotMode}
            snapshotData={isSnapshotMode ? dashboardData : null}
            enhancedMetrics={dashboardData?.enhancedMetrics}
          />
        );
      case 3:
        return (
          <OOHTab 
            key={`ooh-${isSnapshotMode ? 'snap' : 'cur'}-${selectedMonth || 'now'}`}
            data={dashboardData?.oohCampaigns || []}
            loading={loading}
            isSnapshot={isSnapshotMode}
            snapshotMonth={selectedMonth}
            selectedSnapshotMonthStr={isSnapshotMode ? selectedMonth : null}
            enhancedMetrics={dashboardData?.enhancedMetrics}
          />
        );
      case 4:
        return (
          <BudgetTab 
            key={`bud-${isSnapshotMode ? 'snap' : 'cur'}-${selectedMonth || 'now'}`}
            data={dashboardData?.budgetData || []}
            loading={loading}
            isSnapshot={isSnapshotMode}
            snapshotMonth={selectedMonth}
            selectedSnapshotMonthStr={isSnapshotMode ? selectedMonth : null}
            enhancedMetrics={dashboardData?.enhancedMetrics}
          />
        );
      default:
        return null;
    }
  };

  // Show error state with data if available
  if (error && (!dashboardData || Object.keys(dashboardData).length === 0)) {
    return (
      <DashboardLayout
        title={isSnapshotMode ? "Snapshot Dashboard" : "Current Dashboard"}
        subtitle={isSnapshotMode ? 
          `Historical data from ${selectedMonth}` : 
          "Real-time campaign data and analytics"
        }
        dataType={isSnapshotMode ? "snapshot" : "current"}
        month={selectedMonth}
        availableSnapshots={snapshotMonths}
        onNavigate={handleMonthSelect}
        onRefresh={() => fetchDashboardData(selectedMonth)}
        loading={loading}
      >
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="400px"
          p={4}
        >
          <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
            <AlertTitle>❌ Dashboard Error</AlertTitle>
            {error}
          </Alert>
          
          <Box display="flex" gap={2}>
            <Button 
              variant="contained" 
              onClick={fetchDashboardData}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {loading ? 'Retrying...' : 'Retry Loading'}
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            overflowX: 'hidden !important',
            maxWidth: '100vw !important',
            margin: 0,
            padding: 0,
          },
          '*': {
            boxSizing: 'border-box !important',
          },
          '.MuiContainer-root': {
            paddingLeft: '12px !important',
            paddingRight: '12px !important',
            maxWidth: '100% !important',
            width: '100% !important',
            margin: '0 auto !important',
          },
          '.MuiGrid-container': {
            width: '100% !important',
            margin: '0 !important',
          },
          '.MuiGrid-item': {
            maxWidth: '100% !important',
          },
          '.MuiPaper-root': {
            maxWidth: '100% !important',
          },
          '.MuiTableContainer-root': {
            maxWidth: '100% !important',
            overflowX: 'auto !important',
          },
          '.MuiBox-root': {
            maxWidth: '100% !important',
          }
        }}
      />
      <DashboardLayout
        title={isSnapshotMode ? "Snapshot Dashboard" : "Current Dashboard"}
        subtitle={isSnapshotMode ? 
          `Historical data from ${selectedMonth}` : 
          "Real-time campaign data and analytics"
        }
        dataType={isSnapshotMode ? "snapshot" : "current"}
        month={selectedMonth}
        availableSnapshots={snapshotMonths}
        onNavigate={handleMonthSelect}
        onRefresh={() => fetchDashboardData(selectedMonth)}
        loading={loading}
      >
        <Box 
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{ 
            maxWidth: '100%', 
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            p: { xs: 1, sm: 2 }
          }}
        >
          {/* Dashboard Header - REMOVED */}
          {/* {renderDashboardHeader()} */}

          {/* Navigation Tabs */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 3, maxWidth: '100%', overflow: 'hidden' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                color="primary"
                size="small"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: 40,
                    padding: { xs: '6px 8px', sm: '8px 12px' }
                  }
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition={tab.iconPosition}
                  />
                ))}
              </Tabs>
            </Box>
          </motion.div>

          {/* Tab Content */}
          <motion.div variants={itemVariants}>
            <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
              {renderTabContent()}
            </Box>
          </motion.div>
        </Box>

        {/* Success notification for refresh */}
        <Snackbar
          open={refreshing}
          message="Refreshing dashboard data..."
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </DashboardLayout>
    </>
  );
};

export default CurrentDashboard; 